import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Rating, type Grade } from 'ts-fsrs';
import {
  AudioButton,
  Button,
  Celebration,
  Heading,
  Muted,
  ProgressBar,
  ResultBanner,
  Screen,
  Skeleton,
  Stack,
  SubmitBar,
  Text,
} from '@fluent/ui';
import { useAuth } from '../auth/authStore.js';
import { uuid } from '../lib/uuid.js';
import { useAudio } from '../audio/audioPlayer.js';
import { useCelebrationCrossings } from '../progress/useCelebrationCrossings.js';
import { playSfx } from '../lesson/sounds.js';
import { LevelUpModal } from '../lesson/LevelUpModal.js';
import { ChooseCorrect } from '../lesson/ChooseCorrect.js';
import { TranslateTyped } from '../lesson/TranslateTyped.js';
import type { MatchVerdict } from '../lesson/matchAnswer.js';
import { getScheduler } from './fsrsConfig.js';
import {
  useDeckEntries,
  useDeckOverview,
  useRecordReviews,
  useCompleteFlashcardSession,
} from './hooks.js';
import type { DeckEntry, SessionPlan } from './deck.js';
import { buildPool } from './question.js';
import {
  initSession,
  sessionReducer,
  sessionProgress,
  type AnswerVerdict,
  type SessionCard,
  type SessionState,
} from './sessionReducer.js';
import './FlashcardSession.css';

/** Sessions whose completion RPC already fired (StrictMode double-mount guard). */
const submittedSessions = new Set<string>();

/**
 * Flashcard review session — FLASHCARDS.md § Surfaces. TEST MODE
 * (DECISIONS.md 2026-07-16): no flip, no self-grading.
 *
 * Presentation rules:
 * - never-seen cards: teach card (both sides visible, "ახალი" badge) →
 *   "ვსწავლობ" (Good, enters 1m/10m learning) / "უკვე ვიცი" (Easy, graduates)
 * - every other appearance is a QUESTION: multiple choice while the card is
 *   young, typed once mature. Correct/close → Good; wrong / "არ ვიცი" →
 *   Again. Feedback banner, then continue.
 */
export function FlashcardSession() {
  const { user } = useAuth();
  const [startedAt] = useState(() => new Date());
  const overview = useDeckOverview(startedAt);
  const { entries, loaded: entriesLoaded } = useDeckEntries();

  if (!user) return null;

  // Deep-linking straight here races the deck queries — snapshotting the
  // plan before data resolves would start an empty session. Wait it out.
  if (!overview.loaded || !entriesLoaded) {
    return (
      <Screen>
        <div className="fluent-fc-session__empty">
          <Skeleton shape="card" style={{ width: '100%', maxWidth: 480, height: 280 }} />
        </div>
      </Screen>
    );
  }

  return <SessionRunner plan={overview.plan} entries={entries} startedAt={startedAt} />;
}

function SessionRunner({
  plan,
  entries,
  startedAt,
}: {
  plan: SessionPlan;
  entries: DeckEntry[];
  startedAt: Date;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Snapshot the plan once — the session is immutable after start.
  const [sessionId] = useState(() => uuid());
  const [state, dispatch] = useReducer(
    sessionReducer,
    undefined,
    (): SessionState => initSession(plan, startedAt, { pool: buildPool(entries), seed: sessionId }),
  );

  /** Live typed-question input; cleared on every advance. */
  const [typedValue, setTypedValue] = useState('');

  // Input cooldown per card appearance: the dock's controls swap in place
  // (Continue → "არ ვიცი" / teach buttons occupy the same pixels), so the
  // second tap of a double-tap would land on the NEXT card's full-width
  // button and grade a card the user never saw. Briefly ignore dock taps
  // after every advance.
  const [dockCooldown, setDockCooldown] = useState(false);
  useEffect(() => {
    setDockCooldown(true);
    const id = window.setTimeout(() => setDockCooldown(false), 350);
    return () => window.clearTimeout(id);
  }, [state.seq]);

  const recordReviews = useRecordReviews();
  const completeSession = useCompleteFlashcardSession();
  const completeMutate = useRef(completeSession.mutate);
  completeMutate.current = completeSession.mutate;

  // Answer sfx in exactly one place — fires once per entered feedback stage.
  const { feedback } = state;
  useEffect(() => {
    if (!feedback) return;
    playSfx(feedback.verdict === 'exact' || feedback.verdict === 'close' ? 'correct' : 'wrong');
  }, [feedback]);

  // Persist each grading as it happens (optimistic cache + offline queue).
  const persistedCount = useRef(0);
  useEffect(() => {
    if (state.emitted.length > persistedCount.current) {
      recordReviews(state.emitted.slice(persistedCount.current));
      persistedCount.current = state.emitted.length;
    }
    // recordReviews identity changes per render; emitted length drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.emitted.length]);

  // Award XP + streak exactly once, as soon as the outcome is decided.
  // That moment is the FINAL feedback banner (queue drained, last answer
  // graded) — not the extra Continue tap. Exiting during the last banner
  // must not cost the XP/streak the session already earned.
  const sessionDecided =
    state.phase === 'complete' || (state.stage === 'checked' && state.queue.length === 0);
  // Reviews pay XP and extend the streak, so a session can cross a level or
  // a milestone — armed from the same moment the completion RPC fires.
  const { levelUpPending, streakMilestone, level, clearLevelUp } =
    useCelebrationCrossings(sessionDecided);
  const [showLevelUp, setShowLevelUp] = useState(false);
  useEffect(() => {
    if (!sessionDecided || state.reviewedKeys.length === 0) return;
    if (submittedSessions.has(sessionId)) return;
    submittedSessions.add(sessionId);
    completeMutate.current({ sessionId, cards: state.reviewedKeys.length });
  }, [sessionDecided, state.reviewedKeys.length, sessionId]);

  // Celebration fanfare belongs to the celebration screen, not the banner.
  useEffect(() => {
    if (state.phase === 'complete' && state.graded > 0) playSfx('complete');
  }, [state.phase, state.graded]);

  // Opened /flashcards/session with nothing to do → bounce home.
  if (state.phase === 'complete' && state.graded === 0) {
    return (
      <Screen>
        <div className="fluent-fc-session__empty">
          <Stack gap={3} align="center">
            <Text tone="muted">{t('flashcards.session.nothing_due')}</Text>
            <Button variant="primary" onClick={() => navigate('/flashcards')}>
              {t('flashcards.session.back_home')}
            </Button>
          </Stack>
        </div>
      </Screen>
    );
  }

  if (state.phase === 'complete') {
    // Same chain as the lesson player: celebration → (level-up) → back out.
    if (showLevelUp) {
      return (
        <LevelUpModal
          level={level}
          onContinue={() => {
            clearLevelUp();
            navigate('/flashcards');
          }}
        />
      );
    }
    const xp = completeSession.data?.xp_awarded ?? null;
    const stats: Array<{ label: string; value: string }> = [
      { label: t('flashcards.complete.cards'), value: String(state.reviewedKeys.length) },
    ];
    if (state.newLearnedKeys.length > 0) {
      stats.push({
        label: t('flashcards.complete.new_words'),
        value: String(state.newLearnedKeys.length),
      });
    }
    if (xp !== null && xp > 0) {
      stats.push({ label: 'XP', value: `+${xp}` });
    }
    // A crossed streak milestone outranks a clean session — it's the rarer
    // moment. Copy is shared with the lesson celebration (same event, and
    // the strings name neither lessons nor cards).
    return (
      <Celebration
        open
        variant={streakMilestone ? 'streak' : state.againCount === 0 ? 'perfect' : 'default'}
        title={
          streakMilestone
            ? t('lesson.complete.streak_milestone_title', { days: streakMilestone })
            : t('flashcards.complete.title')
        }
        subtitle={
          streakMilestone
            ? t('lesson.complete.streak_milestone_subtitle', { days: streakMilestone })
            : state.againCount === 0
              ? t('flashcards.complete.subtitle_perfect')
              : t('flashcards.complete.subtitle')
        }
        stats={stats}
        ctaLabel={t('flashcards.complete.cta')}
        onContinue={() => {
          if (levelUpPending) setShowLevelUp(true);
          else navigate('/flashcards');
        }}
      />
    );
  }

  const current = state.current!;
  const question = state.question;
  const answering = state.stage === 'answering';
  /** Renderer-facing verdict during the reveal ('idk' paints like wrong). */
  const revealVerdict: MatchVerdict | null = feedback
    ? feedback.verdict === 'idk'
      ? 'wrong'
      : feedback.verdict
    : null;

  const submitTyped = () => {
    if (!answering || typedValue.trim() === '') return;
    dispatch({ type: 'ANSWER', answer: typedValue, now: Date.now(), seq: state.seq });
  };
  const answerIdk = () => {
    if (!answering || dockCooldown) return;
    dispatch({ type: 'ANSWER', answer: null, now: Date.now(), seq: state.seq });
  };
  const advance = () => {
    setTypedValue('');
    dispatch({ type: 'CONTINUE', now: Date.now(), seq: state.seq });
  };

  return (
    <Screen>
      <div className="fluent-fc-session">
        <div className="fluent-fc-session__topbar">
          <Button
            variant="ghost"
            size="md"
            onClick={() => navigate('/flashcards')}
            aria-label={t('flashcards.session.exit')}
          >
            ✕
          </Button>
          <div className="fluent-fc-session__progress">
            <ProgressBar
              value={sessionProgress(state)}
              tone="primary"
              aria-label={t('flashcards.session.progress_aria')}
            />
          </div>
        </div>

        <div className="fluent-fc-session__card-area" key={`${current.key}:${state.seq}`}>
          {current.isNew || !question ? (
            <TeachCard card={current} />
          ) : question.kind === 'choice' ? (
            <ChooseCorrect
              exercise={question.exercise}
              kicker={t('flashcards.session.front_kicker')}
              // One-tap answer (spec): selecting IS submitting.
              selected={feedback?.given ?? null}
              onSelectedChange={(opt) => {
                if (answering && !dockCooldown)
                  dispatch({ type: 'ANSWER', answer: opt, now: Date.now(), seq: state.seq });
              }}
              locked={!answering}
              lastVerdict={revealVerdict}
            />
          ) : (
            <TranslateTyped
              exercise={question.exercise}
              kicker={t('flashcards.session.front_kicker')}
              value={typedValue}
              onChange={setTypedValue}
              onEnter={submitTyped}
              locked={!answering}
              lastVerdict={revealVerdict}
            />
          )}
          {!answering && current.audio_key && (
            // Post-answer pronunciation — the flip-card back used to offer
            // this on every review; the reveal is its new home.
            <div className="fluent-fc-session__reveal-audio">
              <CardAudio audioKey={current.audio_key} />
            </div>
          )}
        </div>

        <div
          className="fluent-fc-session__dock"
          data-cooldown={dockCooldown ? 'true' : undefined}
        >
          {current.isNew || !question ? (
            <TeachDock
              card={current}
              onGrade={(rating) => {
                if (dockCooldown) return;
                playSfx('correct');
                dispatch({ type: 'GRADE', rating, now: Date.now(), seq: state.seq });
              }}
            />
          ) : answering ? (
            question.kind === 'typed' ? (
              <SubmitBar
                ctaLabel={t('lesson.submit')}
                ctaDisabled={typedValue.trim() === ''}
                onCta={submitTyped}
                secondaryLabel={t('flashcards.session.idk')}
                onSecondary={answerIdk}
              />
            ) : (
              <Button variant="neutral" fullWidth onClick={answerIdk}>
                {t('flashcards.session.idk')}
              </Button>
            )
          ) : (
            <ResultBanner
              state={revealVerdict === 'wrong' ? 'wrong' : 'correct'}
              title={
                revealVerdict === 'wrong'
                  ? t('lesson.result.wrong_title')
                  : t('lesson.result.correct_title')
              }
              detail={feedbackDetail(t, feedback, question)}
              onCta={advance}
              ctaLabel={t('lesson.result.continue')}
            />
          )}
        </div>
      </div>
    </Screen>
  );
}

/** Banner detail per verdict. Wrong/idk on a TYPED question reveal the
 *  answer; on a CHOICE question the correct option is already painted
 *  green on the board, so the banner adds nothing (same convention as
 *  lessons). A close typo celebrates but teaches the exact spelling. */
function feedbackDetail(
  t: (key: string, opts?: Record<string, unknown>) => string,
  feedback: { verdict: AnswerVerdict } | null,
  question: { kind: 'choice' | 'typed'; exercise: { answer: string } } | null,
): string | null {
  if (!feedback || !question) return null;
  if (feedback.verdict === 'wrong' || feedback.verdict === 'idk') {
    if (question.kind === 'choice') return null;
    return t('lesson.result.correct_answer', { answer: question.exercise.answer });
  }
  if (feedback.verdict === 'close') {
    return t('flashcards.session.close_detail', { answer: question.exercise.answer });
  }
  return t('lesson.result.correct_detail');
}

function TeachCard({ card }: { card: SessionCard }) {
  const { t } = useTranslation();
  return (
    <div className="fluent-fc-session__teach">
      <span className="fluent-fc-session__badge">{t('flashcards.session.new_badge')}</span>
      <Heading level={2}>{card.en}</Heading>
      <Text size="lg">{card.ka}</Text>
      {card.audio_key && <CardAudio audioKey={card.audio_key} />}
    </div>
  );
}

function CardAudio({ audioKey }: { audioKey: string }) {
  const { playing, error, play, retry } = useAudio(audioKey);
  return <AudioButton size="md" playing={playing} onClick={error ? retry : play} />;
}

function TeachDock({
  card,
  onGrade,
}: {
  card: SessionCard;
  onGrade: (rating: Grade) => void;
}) {
  const { t } = useTranslation();

  // Interval hint for Easy only. "ვსწავლობ" deliberately shows no FSRS
  // interval: the weave re-asks the card ~2 cards later in THIS session
  // (TEACH_QUESTION_GAP), so the scheduler's 10m step would be a lie.
  const easyHint = useMemo(() => {
    const preview = getScheduler().repeat(card.card, new Date());
    return formatInterval(preview[Rating.Easy].card.due, t);
  }, [card, t]);

  return (
    <div className="fluent-fc-session__grades">
      <div className="fluent-fc-session__grade">
        <Button variant="neutral" fullWidth onClick={() => onGrade(Rating.Easy)}>
          {t('flashcards.session.know_already')}
        </Button>
        <Muted>{easyHint}</Muted>
      </div>
      <div className="fluent-fc-session__grade">
        <Button variant="primary" fullWidth onClick={() => onGrade(Rating.Good)}>
          {t('flashcards.session.learn')}
        </Button>
        <Muted>{t('flashcards.session.learn_hint')}</Muted>
      </div>
    </div>
  );
}

/** "10 წთ" / "3 სთ" / "4 დღე" / "2 თვე" — rough Anki-style interval label. */
function formatInterval(due: Date, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const ms = due.getTime() - Date.now();
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return t('flashcards.interval.minutes', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 36) return t('flashcards.interval.hours', { count: hours });
  const days = Math.round(hours / 24);
  if (days < 60) return t('flashcards.interval.days', { count: days });
  return t('flashcards.interval.months', { count: Math.round(days / 30) });
}
